import React, { useEffect, useCallback,useState } from 'react';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import { Tag, Space, notification, Modal, Button } from 'antd';
import { useUserLocale } from '../../hooks/useLocale';
import { useCaseList } from '../../hooks/cases/useCaseList';
import { APP_TERMS } from '../../app-terms';
import { AvailableAppLocales } from '../../availableAppLocales';
import ListPage from '../../components/core/ListPage';
import useListFilters from '../../hooks/useListFilters';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../../axios/apiService';
import { getCaseReviewStatusColor } from '../../utils/case';
import {
  CASE_REVIEW_STATUS_ASSIGNED,
  CASE_REVIEW_STATUS_REJECTED,
} from './CaseConstants';
const AllowedReviewStatusForMarkingAsCompleted = [
  CASE_REVIEW_STATUS_ASSIGNED,
  CASE_REVIEW_STATUS_REJECTED,
];
const completeCaseConfig = {
  title: 'Mark Case As Completed',
  content: 'Are you sure you want to mark this case as completed?',
  okText: 'Yes',
  cancelText: 'No',
};
const CaseList = ({ title, caseReviewStatus, breadCrumbItems, showHasFilter }) => {
  const [modal, modalContextHolder] = Modal.useModal();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userLocale } = useUserLocale();
  const { data, getCaseList } = useCaseList();
  const {
    loading,
    search,
    tableParams,
    setLoading,
    setSearch,
    setTableParams,
  } = useListFilters();
  const [api, contextHolder] = notification.useNotification();
  const [hasRushstatusFilter, setHasRushStatusFilterState] = useState(
    searchParams.get('rushOnly') === 'true'
  );
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getCaseList({
        hasRush:hasRushstatusFilter,
        search,
        page: tableParams.pagination?.current,
        limit: tableParams.pagination?.pageSize,
        sortOrder: tableParams?.sortOrder,
        sortBy: tableParams?.sortField,
        caseReviewStatus: caseReviewStatus,
      });
      setLoading(false);
      // Update table params
      setTableParams({
        ...tableParams,
        pagination: {
          ...tableParams.pagination,
          total: response.totalItems,
        },
      });
    } catch (error) {
      setLoading(false);
    }
  }, [
    tableParams.pagination?.current,
    tableParams.pagination?.pageSize,
    tableParams?.sortOrder,
    tableParams?.sortField,
    search,
    caseReviewStatus,
    hasRushstatusFilter,
  ]);

  const handleTableChange = (pagination, _, sorter) => {
    setTableParams({
      pagination,
      sortOrder: Array.isArray(sorter) ? undefined : sorter.order,
      sortField: Array.isArray(sorter) ? undefined : sorter.field,
    });
  };

  const handleAdd = () => navigate('/user/case/add');
  const handleCaseMarkAsCompleted = async (record) => {
    const confirmed = await modal.confirm(completeCaseConfig);

    if (!confirmed) return;
    try {
      setLoading(true);
      await apiService.markAsSubmittedCaseFromRadioLogist(record?.caseId);
      api.success({
        message: 'Success',
        description: 'Case marked as completed successfully',
      });
      reload();
    } catch (error) {
      api.error({
        description: error?.response?.data?.error || 'Error',
        message:
          error?.response?.data?.message ||
          'Error occured while marking the case as completed',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record) =>
    navigate(`/user/case/edit?id=${record?.caseId}`);
  const handleView = (record) =>
    navigate(`/user/case/view?id=${record?.caseId}`);

  const onActionItemClick = ({ key }, record) => {
    switch (key) {
      case 'MarkAsCompleted':
        handleCaseMarkAsCompleted(record);
        break;
      case 'View':
        handleView(record);
        break;
      case 'Edit':
        handleEdit(record);
        break;

      default:
        break;
    }
  };
const hashRushOnChange = (checked) => {
    setHasRushStatusFilterState(!!checked);
  };
  useEffect(() => {
    reload();
  }, [
    tableParams.pagination?.current,
    tableParams.pagination?.pageSize,
    tableParams?.sortOrder,
    tableParams?.sortField,
    search,
    caseReviewStatus,
    hasRushstatusFilter,
  ]);
  const actionItems = [{ key: 'View', label: 'View' }];
  const columns = [
    {
      title: 'Case ID',
      dataIndex: 'caseId',
    },
    {
      title: 'Patient Name',
      dataIndex: 'patientName',
    },
    {
      title: 'Radiologist',
      dataIndex: 'radioLogistUserSub',
      render: (value, record) => {
        return value ? (
          <div>
            <span>{record?.radioLogistEmail}</span>
          </div>
        ) : (
          <div>N/A</div>
        );
      },
    },
    // {
    //   title: 'Status',
    //   key: 'status',
    //   dataIndex: 'caseStatus',
    //   render: (value, record, index) => {
    //     const statusColor = getCaseStatusColor(record?.caseStatus);
    //     return (
    //       <Tag color={statusColor} key={index}>
    //         {value}
    //       </Tag>
    //     );
    //   },
    //   sorter: true,
    // },
    {
      title: 'Rush Status',
      key: 'hasRush',
      dataIndex: 'hasRush',
      render: (value, record, index) => {
        // const statusColor = getCaseStatusColor(record?.caseStatus);
        return (
          record.hasRush ?
            <Tag color={"red"} key={index}>
              RUSH
            </Tag> : "-"
        );
      },
      sorter: true,
    },
    {
      title: 'Processing Status',
      key: 'reviewStatus',
      dataIndex: 'caseReviewStatus',
      render: (value, record, index) => {
        const statusColor = getCaseReviewStatusColor(record?.caseReviewStatus);
        return (
          <Tag color={statusColor} key={index}>
            {value}
          </Tag>
        );
      },
      sorter: true,
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      render: (text) =>
        format(new Date(text), APP_TERMS.date_1_format, {
          locale: AvailableAppLocales?.[userLocale],
        }),
      sorter: true,
    },
    {
      title: '',
      dataIndex: '',
      render: (text, record) => {
        console.log(
          'record?.caseReviewStatus : ',
          record?.caseReviewStatus,
          caseReviewStatus,
          record
        );
        const markAsCompletedActions =
          AllowedReviewStatusForMarkingAsCompleted.includes(
            record?.caseReviewStatus
          )
            ? [{ key: 'Edit', label: 'Process' }]
            : [];

        const items = record.caseStatus !== "SUBMITED"
          ? [...actionItems, ...markAsCompletedActions]
          : [...markAsCompletedActions];
        return (
          <>
            <Space size="middle">
              {items?.map((item) => (
                <Button
                  key={item?.key}
                  type="primary"
                  onClick={() => onActionItemClick(item, record)}
                >
                  {item?.label}
                </Button>
              ))}
            </Space>
            {modalContextHolder}
          </>
        );
      },
    },
  ];

  return (
    <>
      {contextHolder}
      <ListPage
        title={title}
        data={data?.items || []}
        columns={columns}
        loading={loading}
        pagination={tableParams.pagination}
        rowKey={(record) => record?.caseId}
        onAdd={handleAdd}
        onRefresh={reload}
        onSearch={setSearch}
        onTableChange={handleTableChange}
        breadCrumbItems={breadCrumbItems}
        hideAddAction={true}
        showHasFilter={showHasFilter}
        hasRushFilter={hasRushstatusFilter}
        setHasRushStatusFilter={hashRushOnChange}
      />
    </>
  );
};

CaseList.propTypes = {
  title: PropTypes.string.isRequired,
  caseReviewStatus: PropTypes.string,
  breadCrumbItems: PropTypes.array,
  onItemDelete: PropTypes.func,
  showHasFilter: PropTypes.bool,
};

export default CaseList;
